import { PayloadAction } from "@reduxjs/toolkit";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { LoginApiResponse } from "types/LoginResponse";
import { API_URL } from "utils/constants";
import { TRANSLATE } from "utils/helpers";
import { request } from "utils/request";
import {
  selectForgotEmail,
  selectLoginForm,
  selectResetPasswordForm,
} from "./selectors";
import { actions } from "./slice";
import { LoginForm } from "./types";

export function* handleLogin(
  action: PayloadAction<{ callback: () => void }>,
) {
  const form: LoginForm = yield select(selectLoginForm);

  try {
    yield delay(300);
    const response: LoginApiResponse = yield call(
      request,
      `${API_URL}/user/auth/login`,
      {
        method: "POST",
        body: JSON.stringify({
          email: form.email.value,
          password: form.password.value,
        }),
      },
    );

    const token = response?.data?.data?.accessToken;
    if (!token) {
      yield put(actions.loginError(TRANSLATE("ERRORS.LOGIN_FAILED")));
      return;
    }

    localStorage.setItem("sessionToken", token);
    yield put(
      actions.loginSuccess({
        token,
        email: form.email.value,
        data: response.data,
      }),
    );

    if (action.payload?.callback) {
      yield call(action.payload.callback);
    }
  } catch (error) {
    yield put(actions.loginError(TRANSLATE("ERRORS.LOGIN_FAILED")));
  }
}

export function* handleForgotPassword(
  action: PayloadAction<{ callback: () => void }>,
) {
  const email: string = yield select(selectForgotEmail);

  try {
    yield delay(300);
    yield call(request, `${API_URL}/user/auth/forgot-password`, {
      method: "POST",
      body: JSON.stringify({ email }),
    });

    yield put(actions.forgotPasswordSuccess());

    if (action.payload?.callback) {
      yield call(action.payload.callback);
    }
  } catch (error) {
    yield put(actions.dispatchForgotError(TRANSLATE("ERRORS.SOMETHING_WENT_WRONG", { error: "" })));
  }
}

export function* handleResetPassword(
  action: PayloadAction<{ token: string; callback: () => void }>,
) {
  const form: { password: string; confirm_password: string } = yield select(
    selectResetPasswordForm,
  );

  try {
    yield delay(300);
    yield call(request, `${API_URL}/user/auth/reset-password`, {
      method: "POST",
      body: JSON.stringify({
        token: action.payload.token,
        newPassword: form.password,
        confirmPassword: form.confirm_password,
      }),
    });

    yield put(actions.resetPasswordSuccess());

    if (action.payload?.callback) {
      yield call(action.payload.callback);
    }
  } catch (error) {
    yield put(actions.resetPasswordError());
    yield put(
      actions.updateResetForm({
        name: "error",
        value: TRANSLATE("ERRORS.SOMETHING_WENT_WRONG", { error: "" }),
      }),
    );
  }
}

export function* authSaga() {
  yield takeLatest(actions.submitLogin.type, handleLogin);
  yield takeLatest(actions.forgotPassword.type, handleForgotPassword);
  yield takeLatest(actions.resetPassword.type, handleResetPassword);
}

export default authSaga;
